import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { PersonAvatar } from './PromoPhotos';
import { Input } from './ui/input';
import { ArrowLeft, Search, ChevronDown, ChevronRight, Heart, User, Shield, MessageCircle } from 'lucide-react';
import { BrandLockup } from './BrandLogo';

const HelpCenter = ({ onBack }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState(null);

  const categories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: User,
      color: 'text-blue-500',
      articles: [
        {
          title: 'Creating Your Profile',
          content: 'Learn how to set up an attractive and authentic profile that showcases your personality.'
        },
        {
          title: 'Adding Photos and Videos',
          content: 'Tips for selecting the best photos and creating engaging video profiles.'
        },
        {
          title: 'Setting Your Preferences',
          content: 'Customize your discovery settings to find the most compatible matches.'
        },
        {
          title: 'Understanding Compatibility Scores',
          content: 'How our AI calculates compatibility based on interests, values, and preferences.'
        }
      ]
    },
    {
      id: 'matching-swiping',
      title: 'Matching & Swiping',
      icon: Heart,
      color: 'text-red-500',
      articles: [
        {
          title: 'How Matching Works',
          content: 'Understand how FlameLink\'s advanced algorithm finds your perfect matches.'
        },
        {
          title: 'Swiping Tips',
          content: 'Best practices for swiping to maximize your match potential.'
        },
        {
          title: 'Super Likes and Boosts',
          content: 'Learn about premium features that increase your visibility.'
        },
        {
          title: 'What Happens After a Match',
          content: 'Starting conversations and making the most of your matches.'
        }
      ]
    },
    {
      id: 'messaging',
      title: 'Messaging & Communication',
      icon: MessageCircle,
      color: 'text-green-500',
      articles: [
        {
          title: 'Starting Conversations',
          content: 'Tips for crafting engaging first messages that get responses.'
        },
        {
          title: 'Voice Messages and Media',
          content: 'Using voice messages, photos, and videos to connect better.'
        },
        {
          title: 'Video Chat Safety',
          content: 'Guidelines for safe video chatting with your matches.'
        },
        {
          title: 'Managing Your Conversations',
          content: 'Organizing and managing multiple conversations effectively.'
        }
      ]
    },
    {
      id: 'safety-privacy',
      title: 'Safety & Privacy',
      icon: Shield,
      color: 'text-purple-500',
      articles: [
        {
          title: 'Staying Safe Online',
          content: 'Essential safety tips for online dating and protecting your personal information.'
        },
        {
          title: 'Meeting in Person',
          content: 'Guidelines for safe first dates and in-person meetings.'
        },
        {
          title: 'Reporting and Blocking',
          content: 'How to report inappropriate behavior and block unwanted users.'
        },
        {
          title: 'Privacy Settings',
          content: 'Managing who can see your profile and controlling your visibility.'
        }
      ]
    }
  ];

  const filteredCategories = categories.map(category => ({
    ...category,
    articles: category.articles.filter(article =>
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.content.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.articles.length > 0 || searchQuery === '');

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-pink-100 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={onBack}
              variant="ghost"
              className="flex items-center text-gray-700 hover:text-red-500"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <BrandLockup size="sm" testId="public-brand-lockup" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
              Help Center
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Find answers to your questions and learn how to make the most of FlameLink
          </p>
        </div>

        {/* Search */}
        <Card className="p-6 mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search for help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-3 text-lg"
            />
          </div>
        </Card>

        {/* Categories */}
        <div className="space-y-6">
          {filteredCategories.map((category) => {
            const Icon = category.icon;
            const isExpanded = expandedCategory === category.id;
            
            return (
              <Card key={category.id} className="overflow-hidden">
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="w-full p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Icon className={`w-6 h-6 ${category.color} mr-4`} />
                      <h2 className="text-xl font-semibold text-gray-900">{category.title}</h2>
                      <span className="ml-3 text-sm text-gray-500">
                        ({category.articles.length} articles)
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>
                
                {isExpanded && (
                  <div className="border-t border-gray-200">
                    {category.articles.map((article, index) => (
                      <div key={index} className="p-6 border-b border-gray-100 last:border-b-0">
                        <h3 className="font-semibold text-gray-900 mb-2">{article.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{article.content}</p>
                        <Button
                          variant="link"
                          className="p-0 h-auto text-red-600 hover:text-red-700 mt-2"
                        >
                          Read full article →
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {searchQuery && filteredCategories.length === 0 && (
          <Card className="p-12 text-center">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No results found</h3>
            <p className="text-gray-600 mb-6">
              We couldn't find any articles matching "{searchQuery}". Try using different keywords.
            </p>
            <Button
              onClick={() => setSearchQuery('')}
              variant="outline"
            >
              Clear search
            </Button>
          </Card>
        )}

        {/* Success Stories */}
        <Card className="p-8 mt-8 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Success Stories</h2>
          <p className="text-gray-600 mb-6">
            Hear from real FlameLink users who found love using our platform:
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-4">
              <PersonAvatar personId="isabella" className="w-20 h-20 ring-4 ring-rose-100" />
              <div>
                <p className="text-gray-700 italic mb-2">
                  "The compatibility scoring was spot on! Met my partner through FlameLink and we're planning our wedding next year."
                </p>
                <p className="text-sm text-green-600 font-medium">- Isabella, Vancouver</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <PersonAvatar personId="mia" className="w-20 h-20 ring-4 ring-rose-100" />
              <div>
                <p className="text-gray-700 italic mb-2">
                  "The tipping feature really helped me stand out! It's a thoughtful way to show genuine interest."
                </p>
                <p className="text-sm text-green-600 font-medium">- Mia, Toronto</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Contact Support */}
        <Card className="p-8 mt-12 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Still need help?</h3>
            <p className="text-gray-600 mb-6">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => window.open('mailto:support@flamelink.com', '_blank')}
                className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
              >
                Contact Support
              </Button>
              <Button
                variant="outline"
                onClick={onBack}
              >
                Back to Home
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default HelpCenter;